"use strict";
/**
 * Safety Diagram Generators
 *
 * Mermaid generators for safety analysis visualizations:
 * - FTA (Fault Tree Analysis) diagrams
 * - DD (Dependence Diagrams)
 * - MA (Markov Analysis) state diagrams
 *
 * Uses Mermaid flowchart syntax with custom styling for
 * standard FTA symbols (gates, events).
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.generateFTAMermaid = generateFTAMermaid;
exports.generateDDMermaid = generateDDMermaid;
exports.generateMAMermaid = generateMAMermaid;
exports.generateSafetyDiagram = generateSafetyDiagram;
/**
 * Generate Mermaid diagram from FTA document
 */
function generateFTAMermaid(fta, options = {}) {
    const { showProbabilities = true, showGateDescriptions = false, highlightDominantPath = [], maxDepth = Infinity, direction = 'TB', theme = 'default' } = options;
    const lines = [];
    const highlightSet = new Set(highlightDominantPath);
    // Header
    lines.push(`%%{init: {'theme': '${theme}'}}%%`);
    lines.push(`flowchart ${direction}`);
    lines.push('');
    // Style definitions for FTA symbols
    lines.push('  %% FTA Symbol Styles');
    lines.push('  classDef topEvent fill:#ff6b6b,stroke:#c92a2a,stroke-width:3px,color:#fff');
    lines.push('  classDef intermediateEvent fill:#ffd43b,stroke:#f59f00,stroke-width:2px,color:#000');
    lines.push('  classDef basicEvent fill:#69db7c,stroke:#37b24d,stroke-width:2px,color:#000');
    lines.push('  classDef undevelopedEvent fill:#e9ecef,stroke:#868e96,stroke-width:2px,stroke-dasharray:5,color:#000');
    lines.push('  classDef conditioningEvent fill:#a5d8ff,stroke:#1c7ed6,stroke-width:2px,color:#000');
    lines.push('  classDef houseEvent fill:#ffc9c9,stroke:#e03131,stroke-width:2px,color:#000');
    lines.push('  classDef orGate fill:#da77f2,stroke:#9c36b5,stroke-width:2px,color:#fff');
    lines.push('  classDef andGate fill:#63e6be,stroke:#0ca678,stroke-width:2px,color:#000');
    lines.push('  classDef highlighted stroke:#f03e3e,stroke-width:4px');
    lines.push('');
    // Generate events
    lines.push('  %% Events');
    for (const event of fta.safetyData.events) {
        const node = generateEventNode(event, showProbabilities);
        lines.push(`  ${node}`);
    }
    lines.push('');
    // Generate gates and connections
    lines.push('  %% Gates and Connections');
    for (const gate of fta.safetyData.gates) {
        // Gate node
        const gateLabel = showGateDescriptions && gate.description
            ? `${gate.gateType}<br/>${escapeLabel(gate.description)}`
            : gate.gateType;
        // Gate symbol based on type
        const gateShape = getGateShape(gate.gateType);
        lines.push(`  ${gate.id}${gateShape[0]}${gateLabel}${gateShape[1]}`);
        // Connections from inputs to gate
        for (const inputEventId of gate.inputEventIds || []) {
            const linkStyle = highlightSet.has(inputEventId) ? '==>' : '-->';
            lines.push(`  ${inputEventId} ${linkStyle} ${gate.id}`);
        }
        for (const inputGateId of gate.inputGateIds || []) {
            const linkStyle = highlightSet.has(inputGateId) ? '==>' : '-->';
            lines.push(`  ${inputGateId} ${linkStyle} ${gate.id}`);
        }
        // Connection from gate to output event
        const linkStyle = highlightSet.has(gate.id) ? '==>' : '-->';
        lines.push(`  ${gate.id} ${linkStyle} ${gate.outputEventId}`);
    }
    lines.push('');
    // Apply classes
    lines.push('  %% Apply Styles');
    for (const event of fta.safetyData.events) {
        const className = getEventClassName(event.eventType);
        lines.push(`  class ${event.id} ${className}`);
        if (highlightSet.has(event.id)) {
            lines.push(`  class ${event.id} highlighted`);
        }
    }
    for (const gate of fta.safetyData.gates) {
        const className = gate.gateType === 'OR' || gate.gateType === 'XOR'
            ? 'orGate'
            : 'andGate';
        lines.push(`  class ${gate.id} ${className}`);
    }
    return lines.join('\n');
}
function generateEventNode(event, showProbability) {
    let label = escapeLabel(event.description);
    if (showProbability && event.probability !== undefined) {
        const probStr = event.probability.toExponential(2);
        label += `<br/>P = ${probStr}`;
    }
    // Shape based on event type
    switch (event.eventType) {
        case 'top':
            // Double rectangle for top event
            return `${event.id}[["${label}"]]`;
        case 'intermediate':
            // Rectangle
            return `${event.id}["${label}"]`;
        case 'basic':
            // Circle for basic event
            return `${event.id}(("${label}"))`;
        case 'undeveloped':
            // Diamond for undeveloped
            return `${event.id}{"${label}"}`;
        case 'conditioning':
            // Ellipse for conditioning
            return `${event.id}(["${label}"])`;
        case 'house':
            // Hexagon for house event
            return `${event.id}{{"${label}"}}`;
        default:
            return `${event.id}["${label}"]`;
    }
}
function getEventClassName(eventType) {
    switch (eventType) {
        case 'top': return 'topEvent';
        case 'intermediate': return 'intermediateEvent';
        case 'basic': return 'basicEvent';
        case 'undeveloped': return 'undevelopedEvent';
        case 'conditioning': return 'conditioningEvent';
        case 'house': return 'houseEvent';
        default: return 'intermediateEvent';
    }
}
function getGateShape(gateType) {
    // Use trapezoid for gates
    switch (gateType) {
        case 'OR':
        case 'XOR':
            return ['[/', '/]']; // Parallelogram
        case 'AND':
        case 'PRIORITY_AND':
        case 'INHIBIT':
            return ['[\\', '\\]']; // Parallelogram (other direction)
        case 'VOTE':
            return ['{{', '}}']; // Hexagon
        default:
            return ['[/', '/]'];
    }
}
function escapeLabel(text) {
    return text
        .replace(/"/g, "'")
        .replace(/\n/g, '<br/>')
        .slice(0, 50) + (text.length > 50 ? '...' : '');
}
/**
 * Generate Mermaid diagram from DD document
 */
function generateDDMermaid(dd, options = {}) {
    const { direction = 'LR', showDependencyTypes = true, highlightFailurePaths = true, theme = 'default' } = options;
    const lines = [];
    // Header
    lines.push(`%%{init: {'theme': '${theme}'}}%%`);
    lines.push(`flowchart ${direction}`);
    lines.push('');
    // Style definitions
    lines.push('  %% DD Node Styles');
    lines.push('  classDef functionNode fill:#74c0fc,stroke:#1971c2,stroke-width:2px,color:#000');
    lines.push('  classDef componentNode fill:#b2f2bb,stroke:#2f9e44,stroke-width:2px,color:#000');
    lines.push('  classDef resourceNode fill:#ffd8a8,stroke:#e8590c,stroke-width:2px,color:#000');
    lines.push('  classDef externalNode fill:#e9ecef,stroke:#495057,stroke-width:2px,color:#000');
    lines.push('  classDef failurePath stroke:#e03131,stroke-width:3px');
    lines.push('');
    // Generate nodes
    lines.push('  %% Nodes');
    for (const node of dd.safetyData.nodes) {
        const shape = getNodeShape(node.nodeType);
        const label = escapeLabel(node.name);
        lines.push(`  ${node.id}${shape[0]}${label}${shape[1]}`);
    }
    lines.push('');
    // Generate dependencies
    lines.push('  %% Dependencies');
    for (const dep of dd.safetyData.dependencies) {
        let linkLabel = '';
        if (showDependencyTypes) {
            linkLabel = `|${dep.dependencyType}|`;
        }
        const linkStyle = (highlightFailurePaths && dep.failurePropagation)
            ? '==>'
            : '-->';
        lines.push(`  ${dep.sourceNodeId} ${linkStyle}${linkLabel} ${dep.targetNodeId}`);
    }
    lines.push('');
    // Apply styles
    lines.push('  %% Apply Styles');
    for (const node of dd.safetyData.nodes) {
        const className = `${node.nodeType}Node`;
        lines.push(`  class ${node.id} ${className}`);
    }
    // Highlight failure propagation paths
    if (highlightFailurePaths) {
        for (const dep of dd.safetyData.dependencies) {
            if (dep.failurePropagation) {
                lines.push(`  class ${dep.sourceNodeId} failurePath`);
                lines.push(`  class ${dep.targetNodeId} failurePath`);
            }
        }
    }
    return lines.join('\n');
}
function getNodeShape(nodeType) {
    switch (nodeType) {
        case 'function':
            return ['["', '"]']; // Rectangle
        case 'component':
            return ['(("', '"))']; // Circle
        case 'resource':
            return ['[/"', '"/]']; // Parallelogram
        case 'external':
            return ['[["', '"]]']; // Double rectangle
        default:
            return ['["', '"]'];
    }
}
/**
 * Generate Mermaid state diagram from Markov Analysis document
 */
function generateMAMermaid(ma, options = {}) {
    const { direction = 'LR', showRates = true, showSteadyState = true, theme = 'default' } = options;
    const lines = [];
    // Header - use state diagram for Markov
    lines.push(`%%{init: {'theme': '${theme}'}}%%`);
    lines.push(`stateDiagram-v2`);
    lines.push(`  direction ${direction}`);
    lines.push('');
    // Generate states
    lines.push('  %% States');
    for (const state of ma.safetyData.states) {
        let label = state.name;
        if (showSteadyState && state.steadyStateProbability !== undefined) {
            const probStr = (state.steadyStateProbability * 100).toFixed(2);
            label += `\\n(π = ${probStr}%)`;
        }
        lines.push(`  ${state.id}: ${label}`);
        // Mark failed states
        if (state.isFailState) {
            lines.push(`  state ${state.id} <<failed>>`);
        }
    }
    lines.push('');
    // Generate transitions
    lines.push('  %% Transitions');
    for (const trans of ma.safetyData.transitions) {
        let label = trans.description || trans.transitionType;
        if (showRates) {
            const rateStr = trans.rate.toExponential(2);
            label += ` (${rateStr}/${trans.rateUnit})`;
        }
        lines.push(`  ${trans.fromStateId} --> ${trans.toStateId}: ${label}`);
    }
    return lines.join('\n');
}
/**
 * Generate Mermaid diagram for any safety analysis type
 */
function generateSafetyDiagram(type, document, options = {}) {
    switch (type) {
        case 'fta':
            return generateFTAMermaid(document, options);
        case 'dd':
            return generateDDMermaid(document, options);
        case 'markov':
            return generateMAMermaid(document, options);
        default:
            throw new Error(`Unsupported diagram type: ${type}`);
    }
}
//# sourceMappingURL=safety-diagrams.js.map